"use client"

import { useState, useEffect } from "react"
import Link from "next/link"
import { MapPin, Building2, TrendingUp, Users, Search, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import GetInTouchModal from "@/components/get-in-touch-modal"

// Neighborhood data
interface Neighborhood {
  id: string
  name: string
  city: string
  priceRangeDisplay: string
  shortDescription: string
  propertyCount: number
  featuredImage?: string
  displayOrder: number
}

export default function NeighborhoodsPage() {
  const [neighborhoods, setNeighborhoods] = useState<Neighborhood[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [selectedCity, setSelectedCity] = useState<string>("all")
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Fetch neighborhoods from API
  useEffect(() => {
    const fetchNeighborhoods = async () => {
      try {
        const response = await fetch('/api/admin/neighborhoods')
        const data = await response.json()

        if (data.success) {
          // Filter only active neighborhoods and sort by display_order
          const activeNeighborhoods = data.neighborhoods
            .filter((n: any) => n.isActive)
            .map((n: any) => ({
              id: n.id,
              name: n.name,
              city: n.city,
              priceRangeDisplay: n.priceRangeDisplay,
              shortDescription: n.shortDescription,
              propertyCount: n.propertyCount,
              featuredImage: n.featuredImage,
              displayOrder: n.displayOrder
            }))
            .sort((a: Neighborhood, b: Neighborhood) => {
              if (a.displayOrder !== b.displayOrder) {
                return a.displayOrder - b.displayOrder
              }
              return b.propertyCount - a.propertyCount
            })

          setNeighborhoods(activeNeighborhoods)
        }
      } catch (error) {
        console.error('Error fetching neighborhoods:', error)
      } finally {
        setIsLoading(false)
      }
    }

    fetchNeighborhoods()
  }, [])

  // Get unique cities sorted by their minimum display_order (not alphabetically)
  const cities = Array.from(new Set(neighborhoods.map((n) => n.city)))
    .sort((a, b) => {
      const aMinOrder = Math.min(...neighborhoods.filter(n => n.city === a).map(n => n.displayOrder))
      const bMinOrder = Math.min(...neighborhoods.filter(n => n.city === b).map(n => n.displayOrder))
      return aMinOrder - bMinOrder
    })

  // Filter neighborhoods
  const filteredNeighborhoods = neighborhoods.filter((neighborhood) => {
    const matchesCity = selectedCity === "all" || neighborhood.city === selectedCity
    const matchesSearch =
      searchTerm === "" ||
      neighborhood.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      neighborhood.city.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesCity && matchesSearch
  })

  // Group by city and sort neighborhoods within each city by property count (descending)
  const groupedNeighborhoods = cities.reduce(
    (acc, city) => {
      acc[city] = filteredNeighborhoods
        .filter((n) => n.city === city)
        .sort((a, b) => b.propertyCount - a.propertyCount)
      return acc
    },
    {} as Record<string, Neighborhood[]>
  )

  // Calculate totals
  const totalProperties = neighborhoods.reduce((sum, n) => sum + n.propertyCount, 0)

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-[#d60500] mx-auto mb-4" />
          <p className="text-gray-600">Loading neighborhoods...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#d60500] via-[#b50400] to-[#900300] text-white py-16 md:py-24 mt-16 md:mt-20">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading">
              Explore Premium Neighborhoods
            </h1>
            <p className="text-lg md:text-xl font-light max-w-3xl mx-auto leading-relaxed">
              Discover the Philippines' most prestigious addresses featuring world-class properties starting at ₱50M+
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="flex justify-center mb-3">
                <Building2 className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <p className="text-2xl md:text-3xl font-bold mb-1 font-heading">{neighborhoods.length}</p>
              <p className="text-sm md:text-base font-light">Premium Neighborhoods</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="flex justify-center mb-3">
                <MapPin className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <p className="text-2xl md:text-3xl font-bold mb-1 font-heading">{cities.length}</p>
              <p className="text-sm md:text-base font-light">Major Cities</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="flex justify-center mb-3">
                <Users className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <p className="text-2xl md:text-3xl font-bold mb-1 font-heading">{totalProperties.toLocaleString()}+</p>
              <p className="text-sm md:text-base font-light">Properties Listed</p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-6 text-center">
              <div className="flex justify-center mb-3">
                <TrendingUp className="w-8 h-8 md:w-10 md:h-10" />
              </div>
              <p className="text-2xl md:text-3xl font-bold mb-1 font-heading">₱50M+</p>
              <p className="text-sm md:text-base font-light">Starting Price</p>
            </div>
          </div>
        </div>
      </section>

      {/* Search & Filter Section */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl py-8">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search neighborhoods..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 h-12 rounded-xl"
              />
            </div>

            {/* City Filter */}
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="h-12 rounded-xl">
                <SelectValue placeholder="Filter by city" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Cities</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city} value={city}>
                    {city}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Results count */}
          <p className="text-sm text-gray-600 mt-4 font-light">
            Showing {filteredNeighborhoods.length} of {neighborhoods.length} neighborhoods
          </p>
        </div>
      </section>

      {/* Neighborhoods Grid - Grouped by City */}
      <section className="container mx-auto px-4 md:px-8 max-w-7xl pb-16">
        {selectedCity === "all" ? (
          // Show grouped by city
          Object.entries(groupedNeighborhoods).map(([city, cityNeighborhoods]) => {
            if (cityNeighborhoods.length === 0) return null

            return (
              <div key={city} className="mb-12">
                <div className="flex items-center gap-3 mb-6">
                  <MapPin className="w-6 h-6 text-[#d60500]" />
                  <h2 className="text-2xl md:text-3xl font-bold font-heading">{city}</h2>
                  <span className="bg-red-50 text-[#d60500] px-3 py-1 rounded-full text-sm font-semibold">
                    {cityNeighborhoods.length} {cityNeighborhoods.length === 1 ? "neighborhood" : "neighborhoods"}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {cityNeighborhoods.map((neighborhood) => (
                    <NeighborhoodCard key={neighborhood.id} neighborhood={neighborhood} />
                  ))}
                </div>
              </div>
            )
          })
        ) : (
          // Show all results in grid
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredNeighborhoods.map((neighborhood) => (
              <NeighborhoodCard key={neighborhood.id} neighborhood={neighborhood} />
            ))}
          </div>
        )}

        {/* No results */}
        {filteredNeighborhoods.length === 0 && (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2 font-heading">No neighborhoods found</h3>
            <p className="text-gray-600 font-light">Try adjusting your search or filter criteria</p>
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-heading">
            Ready to Find Your Dream Home?
          </h2>
          <p className="text-lg md:text-xl font-light mb-8 max-w-2xl mx-auto">
            Browse our exclusive collection of luxury properties in Metro Manila's most prestigious neighborhoods.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              asChild
              className="h-12 px-8 rounded-none bg-[#d60500] hover:bg-[#b50400] text-white uppercase tracking-wider"
            >
              <Link href="/properties">Browse All Properties</Link>
            </Button>
            <Button
              onClick={() => setIsModalOpen(true)}
              className="h-12 px-8 rounded-none bg-gradient-to-r from-[#d60500] to-[#b50400] text-white hover:scale-105 transition-transform uppercase tracking-wider"
            >
              Get In Touch
            </Button>
          </div>
        </div>
      </section>

      {/* Get In Touch Modal */}
      <GetInTouchModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        source="neighborhoods-page"
      />
    </div>
  )
}

// Neighborhood Card Component
function NeighborhoodCard({ neighborhood }: { neighborhood: Neighborhood }) {
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-shadow group">
      {/* Image placeholder - can be replaced with actual images */}
      <div className="h-48 bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <Building2 className="w-16 h-16 text-[#d60500] opacity-50" />
      </div>

      <div className="p-6">
        {/* Location */}
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-2 font-light">
          <MapPin className="w-4 h-4 text-[#d60500]" />
          {neighborhood.city}
        </div>

        {/* Name */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-[#d60500] transition-colors font-heading">
          {neighborhood.name}
        </h3>

        {/* Price Range */}
        <p className="text-lg font-bold text-[#d60500] mb-3 font-heading">
          {neighborhood.priceRangeDisplay}
        </p>

        {/* Description */}
        <p className="text-sm text-gray-600 mb-4 line-clamp-3 font-light leading-relaxed">
          {neighborhood.shortDescription}
        </p>

        {/* Properties count */}
        <div className="flex items-center gap-2 text-sm text-gray-500 mb-4 font-light">
          <Building2 className="w-4 h-4" />
          {neighborhood.propertyCount} {neighborhood.propertyCount === 1 ? 'property' : 'properties'} available
        </div>

        {/* CTA */}
        <Link
          href={`/properties?search=${encodeURIComponent(neighborhood.name)}&city=${encodeURIComponent(neighborhood.city)}`}
          className="block w-full bg-[#d60500] hover:bg-[#b50400] text-white text-center py-3 rounded-none font-semibold transition-colors uppercase tracking-wider text-sm"
        >
          Explore Properties
        </Link>
      </div>
    </div>
  )
}
